import * as React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface PageHeaderProps {
  title: string;
  description?: string;
  /** Badges to the right of the title: environment, sandbox, status. */
  meta?: React.ReactNode;
  actions?: React.ReactNode;
  breadcrumb?: readonly { label: string; to?: string }[];
  className?: string;
}

/**
 * Contract: docs/FRONTEND_COMPONENT_INVENTORY.md §5.5. The set's
 * widest-reaching component: 27 of 35 screens.
 *
 * The screen's `<h1>` lives here and nowhere else — today
 * `DashboardPage`/`DtesListPage` write it loose with `text-h2 text-fg`;
 * `PageHeader` replaces those 27 loose copies with one.
 *
 * `breadcrumb` is real navigation (a `<nav>` + an ordered list), not a row of
 * `<span>`s separated by "/": a screen reader announces the `<nav>` with its
 * name and the list's item count, and every intermediate item is a real link.
 * The LAST item is the current screen: even when it carries a `to`, it does not
 * navigate — it is deliberately ignored and marked `aria-current="page"`,
 * because a link to the page you are already on is not a useful link.
 */
export function PageHeader({
  title,
  description,
  meta,
  actions,
  breadcrumb,
  className,
}: PageHeaderProps): React.JSX.Element {
  return (
    <header className={cn('flex flex-col gap-stack-sm', className)}>
      {breadcrumb && breadcrumb.length > 0 ? (
        <nav aria-label="Migas de pan">
          <ol className="flex flex-wrap items-center gap-inset-sm text-body-s text-fg-muted">
            {breadcrumb.map((item, index) => {
              const isLast = index === breadcrumb.length - 1;
              const isNavigable = item.to != null && !isLast;

              return (
                <li
                  key={`${item.label}-${index}`}
                  className="flex items-center gap-inset-sm"
                >
                  {index > 0 ? (
                    <ChevronRight
                      aria-hidden="true"
                      className="size-icon-sm text-fg-disabled"
                    />
                  ) : null}
                  {isNavigable ? (
                    <Link
                      to={item.to as string}
                      className="inline-flex items-center focus-ring rounded-sm hover:text-fg hover:underline pointer-coarse:min-h-touch"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <span
                      aria-current={isLast ? 'page' : undefined}
                      className={isLast ? 'font-medium text-fg' : undefined}
                    >
                      {item.label}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-inset">
        <div className="flex flex-col gap-stack-sm">
          <div className="flex flex-wrap items-center gap-inset-sm">
            <h1 className="text-h2 text-fg">{title}</h1>
            {meta ? (
              <div className="flex flex-wrap items-center gap-inset-sm">
                {meta}
              </div>
            ) : null}
          </div>
          {description ? (
            <p className="text-body-s text-fg-muted">{description}</p>
          ) : null}
        </div>
        {actions ? (
          // `shrink-0` keeps a long title from squeezing the buttons; `max-w-full`
          // plus `flex-wrap` is what lets them stack instead of widening the page.
          // Without the pair, four actions — «Editar configuración»,
          // «Sincronizar ahora», «Pausar», «Eliminar» on a connection — are one
          // unbreakable 600px row, and since `shrink-0` refuses to shrink below
          // that, the DOCUMENT grew to 600px at a 390px viewport. A max width is
          // applied after flexing, so it overrides the refusal to shrink and the
          // row wraps inside itself.
          <div className="flex max-w-full shrink-0 flex-wrap items-center gap-inset-sm">
            {actions}
          </div>
        ) : null}
      </div>
    </header>
  );
}
